import type { CheckInResult, DeviceView } from '../services/cashierApi';
import { DeviceErrorAction, describeDeviceError } from './deviceErrors';

export type StartScreenState = 'ready' | 'waiting_approval' | 'blocked' | 'mismatch' | 'busy' | 'unknown_device';

export type StartScreen = {
  state: StartScreenState;
  title: string;
  message: string;
  action: DeviceErrorAction;
  /** Short code of this device (shown big on the waiting screen so the manager can match it). */
  shortCode: string | null;
  checkedInAt: string | null;
};

const BLOCKER_STATES: Record<string, StartScreenState> = {
  DEVICE_UNKNOWN: 'unknown_device',
  DEVICE_NOT_APPROVED: 'waiting_approval',
  DEVICE_BLOCKED: 'blocked',
  DEVICE_MISMATCH: 'mismatch',
  DEVICE_BUSY: 'busy',
};

/** Pure: map the check-in response to what the Start shift screen shows. */
export function toStartScreen(result: CheckInResult): StartScreen {
  const common = {
    shortCode: result.device?.shortCode || null,
    checkedInAt: result.shift?.checkedInAt || null,
  };

  if (result.ready) {
    return { ...common, state: 'ready', title: 'Ready to start', message: 'This device is approved.', action: null };
  }

  const info = describeDeviceError(result.blocker, { deviceName: result.pinnedDevice?.name });
  const state = result.blocker ? BLOCKER_STATES[result.blocker] : undefined;
  if (info && state) {
    return { ...common, state, title: info.title, message: info.message, action: info.action };
  }

  // Not ready with a blocker this portal does not know yet: show the server's reason and go back.
  return {
    ...common,
    state: 'blocked',
    title: 'Cannot start on this device',
    message: result.message || 'This shift cannot start on this device right now. Ask your manager.',
    action: 'back',
  };
}

export type DeviceStatusKind = 'approved' | 'pending' | 'blocked' | 'unknown';

/** Group device statuses for display: approved, waiting for approval, blocked (any other status), or no device. */
export function deviceStatusKind(device: Pick<DeviceView, 'status'> | null | undefined): DeviceStatusKind {
  if (!device) return 'unknown';
  if (device.status === 'active') return 'approved';
  if (device.status === 'pending_approval') return 'pending';
  return 'blocked';
}
